import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Palette, Plus, Eye, Edit, Building2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Loader from '../../components/ui/Loader';
import Select from '../../components/ui/Select';
import { IEmailTemplate } from 'types/emailTemplate/emailTemplate.types';
import {
  selectAllWorkspaces,
  selectGetAllWorkspacesLoading,
} from '../../store/workspace/workspace.selector';
import { getAllWorkspacesRequest } from '../../store/workspace/workspace.action';
import { getEmailTemplatesRequest } from '../../store/emailTemplates/emailTemplates.action';
import type { IWorkspace } from '../../types/workspace/workspace.types';
import { selectWorkspaceId } from 'store/user';

export interface TemplateSelectionGridProps {
  blankUnlayerConfig: any;
  emailTemplates: IEmailTemplate[] | null;
  emailTemplatesLoading: boolean;
  onSelectBlank: (unlayerConfig: any) => void;
  onSelectTemplate: (template: IEmailTemplate) => void;
  onPreviewTemplate: (templateName: string, htmlContent: string) => void;
}

const TemplateSelectionGrid: React.FC<TemplateSelectionGridProps> = ({
  blankUnlayerConfig,
  emailTemplates,
  emailTemplatesLoading,
  onSelectBlank,
  onSelectTemplate,
  onPreviewTemplate,
}) => {
  const dispatch = useDispatch<any>();
  const allWorkspaces = useSelector(selectAllWorkspaces);
  const workspacesLoading = useSelector(selectGetAllWorkspacesLoading);
  const currentWorkspaceId = useSelector(selectWorkspaceId);
  // Initialize with current workspace so it's selected from first render
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(
    () => currentWorkspaceId ?? ''
  );

  useEffect(() => {
    dispatch(getAllWorkspacesRequest());
  }, [dispatch]);

  // Keep selection in sync with current workspace (e.g. when it loads or user switches)
  useEffect(() => {
    if (currentWorkspaceId) {
      setSelectedWorkspaceId(currentWorkspaceId);
    }
  }, [currentWorkspaceId]);
  // Fetch email templates when workspace selection changes
  useEffect(() => {
    if (selectedWorkspaceId) {
      dispatch(getEmailTemplatesRequest({ workspaceId: selectedWorkspaceId }));
    }
  }, [selectedWorkspaceId, dispatch]);

  const handleWorkspaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setSelectedWorkspaceId(value);
  };

  const workspaceOptions = (allWorkspaces || [])
    .filter((w: IWorkspace) => w._id)
    .map((w: IWorkspace) => ({
      value: w._id!,
      label: w.name ?? 'Unnamed workspace',
    }));

  if (emailTemplatesLoading && !emailTemplates?.length) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader size="medium" showText={false} />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Workspace dropdown */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 text-primary border border-primary/20 shrink-0">
            <Building2 className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Workspace
            </label>
            <Select
              label=""
              size="sm"
              placeholder="Select workspace"
              value={selectedWorkspaceId}
              onChange={handleWorkspaceChange}
              options={workspaceOptions}
              disabled={workspacesLoading}
              className="p-2 rounded-lg border-border bg-card shadow-sm hover:border-primary/50 focus:ring-0 transition-colors"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {/* Start from Scratch – primary CTA card (design ref from former empty state) */}
        <button
          type="button"
          className="group relative flex flex-col items-center justify-center text-center rounded-xl border-2 border-dashed border-primary/20 bg-gradient-to-b from-primary/5 to-primary/[0.02] overflow-hidden hover:border-primary hover:shadow-md transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 min-h-[160px] py-6 px-4"
          onClick={() => {
            if (blankUnlayerConfig) {
              onSelectBlank(blankUnlayerConfig);
            }
          }}
        >
          <div
            className="absolute inset-0 rounded-xl opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(to right, currentColor 1px, transparent 1px),
              linear-gradient(to bottom, currentColor 1px, transparent 1px)`,
              backgroundSize: '24px 24px',
            }}
          />
          <div className="relative flex flex-col items-center max-w-sm gap-2">
            <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary border-2 border-primary/20 group-hover:bg-primary/15 group-hover:border-primary/40 transition-colors">
              <Plus className="h-6 w-6" strokeWidth={1.5} />
            </div>
            <h3 className="text-base font-semibold text-foreground">
              Start from Scratch
            </h3>
            <p className="text-xs text-muted-foreground leading-snug mb-1">
              Create your first template from a blank canvas, or add one from a
              campaign.
            </p>
            <span className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-card border border-border text-xs font-medium text-foreground shadow-sm hover:border-primary/50 hover:bg-primary/5 transition-colors">
              <Edit className="h-3.5 w-3.5" />
              Start designing
            </span>
          </div>
        </button>

        {/* Email Templates from Store */}
        {emailTemplates &&
          emailTemplates.length > 0 &&
          emailTemplates.map((template) => (
            <article
              key={template._id}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md hover:border-primary/50 transition-all cursor-pointer group relative focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
            >
              <div className="h-44 relative overflow-hidden bg-muted/30">
                {template.unlayerConfig?.html ? (
                  <div className="relative w-full h-full overflow-hidden">
                    <iframe
                      srcDoc={template.unlayerConfig.html}
                      className="absolute border-0 pointer-events-none"
                      title={`Preview of ${template.name}`}
                      style={{
                        width: '400px',
                        height: '800px',
                        transform: 'scale(0.8)',
                        transformOrigin: 'top left',
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center bg-muted/50">
                    <div className="text-center p-4">
                      <Layout className="h-11 w-11 text-primary mx-auto mb-2" />
                      <p className="text-sm text-foreground font-medium truncate max-w-full px-2">
                        {template.name}
                      </p>
                    </div>
                  </div>
                )}
                <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100 gap-2 p-3">
                  <Button
                    variant="outline"
                    size="small"
                    className="bg-card border-border shadow-md"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (template.unlayerConfig?.html) {
                        onPreviewTemplate(
                          template.name,
                          template.unlayerConfig.html
                        );
                      }
                    }}
                  >
                    <Eye className="h-4 w-4" />
                    Preview
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (template.unlayerConfig) {
                        onSelectTemplate(template);
                      }
                    }}
                  >
                    <Edit className="h-4 w-4" />
                    Use Template
                  </Button>
                </div>
              </div>
              <div className="p-4 border-t border-border">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className="text-sm font-semibold text-foreground truncate">
                    {template.name}
                  </h3>
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded capitalize shrink-0">
                    {template.status || 'draft'}
                  </span>
                </div>
                <div className="flex items-center gap-2 pt-2 border-t border-border">
                  <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                    {template.subject || 'No subject'}
                  </span>
                  <Palette className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </div>
              </div>
            </article>
          ))}
      </div>
    </div>
  );
};

export default TemplateSelectionGrid;
